export type CreatorService = {
  title: string
  /** Display string in the price slot. Usually a dollar amount ("$90") but
   *  can also be a short descriptor ("Free", "Order", "Apply") for offerings
   *  that aren't priced sessions. */
  price: string
  duration?: string
  description?: string
  /** Optional per-service link that overrides the creator-level bookingUrl
   *  in the ServicesPage CTA. Used when a creator's offerings each have
   *  their own landing page (e.g. Adrienne's deck / certification / free
   *  breathwork links). */
  url?: string
}

export type Creator = {
  id: string
  name: string
  fullName?: string
  role: string
  bio: string
  image: string
  /** Omit (or set to undefined) when the practitioner's booking link
   *  isn't ready yet — the UI renders a "Booking link coming soon" pill
   *  instead of a clickable anchor in that case. */
  bookingUrl?: string
  featuredServices: CreatorService[]
  allServices: CreatorService[]
  /** When set, signals the practitioner isn't fully live yet. UI surfaces
   *  show a "Joining soon" pill instead of a profile link. */
  status?: 'live' | 'joining-soon'
}

export const CREATORS: Creator[] = [
  {
    id: 'sarah',
    name: 'Sarah',
    fullName: 'Sarah McCallum',
    role: 'Astrologer, Akashic Healer & Founder',
    bio: 'Sarah founded The Cosmic Finder after twenty years of intuitive practice. She holds personal sessions in astrology, Akashic record healing, and intuitive health coaching.',
    image: '/creators/sarah.webp',
    bookingUrl: 'https://sarahd0324.simplybook.me/v2/',
    featuredServices: [
      { title: 'Foundational Astrology Session', price: '$90', duration: '60 min' },
      { title: 'Akashic Record Healing', price: '$150' },
    ],
    allServices: [
      {
        title: 'Foundational Astrology Session',
        price: '$90',
        duration: '60 min',
        description:
          'An hour-long session designed to help you find clarity and deeper self-understanding through your natal chart.',
      },
      {
        title: 'Astrology Package (7 sessions)',
        price: '$650',
        description: 'Seven sessions designed to build one upon the next.',
      },
      {
        title: 'Akashic Record Healing',
        price: '$150',
        description: 'Past life and nodal healing through the Akashic records.',
      },
      {
        title: 'Intuitive Health Coaching Session',
        price: '$90',
        description:
          'A personalized dive into your health and wellness routine, in person or via Zoom.',
      },
      {
        title: 'Intuitive Health Coaching Package',
        price: '$425',
        description:
          'A complete healing process combining astrology, Akashic record nodal healing, and coaching.',
      },
      {
        title: 'Astrology Party (group, up to 6)',
        price: '$222',
        duration: '2 hr',
        description:
          "A two-hour group session diving into each attendee's natal chart. Sarah travels to the host's home.",
      },
    ],
  },
  {
    id: 'adrienne',
    name: 'Adrienne',
    fullName: 'Adrienne Rivera',
    role: 'Breathwork Facilitator & Founder of Breath of Gold',
    bio: 'Adrienne Rivera is the founder of Breath of Gold®, a top trauma-informed Breathwork Facilitator Program. TEDx Speaker, best-selling author, and high-energy leader who has guided thousands of breathwork clients to live their best lives.',
    image: '/creators/adrienne.webp',
    // Creator-level CTA points at her free breathwork link — the most
    // session-like entry. Individual service CTAs use their own `url`.
    bookingUrl: 'https://adriennerivera.krtra.com/t/RvGO7zE6CBdT',
    featuredServices: [
      { title: 'Try Breathwork for Free', price: 'Free' },
      { title: 'Breath of Gold Oracle Deck', price: 'Order' },
    ],
    allServices: [
      {
        title: 'Try Breathwork for Free',
        price: 'Free',
        url: 'https://adriennerivera.krtra.com/t/RvGO7zE6CBdT',
        description:
          "An intro to Adrienne's breathwork practice — no commitment, just breathe.",
      },
      {
        title: 'Breath of Gold Oracle Deck',
        price: 'Order',
        url: 'https://adriennerivera.krtra.com/t/3GLJbfcuPmZa',
        description:
          "Adrienne's 67-card oracle deck mailed to your door. Each card invites you to breathe in a virtue.",
      },
      {
        title: 'Breathwork Facilitator Certification',
        price: 'Apply',
        url: 'https://adriennerivera.krtra.com/t/SE9tFhiyRgCp',
        description:
          "Adrienne's flagship trauma-informed breathwork certification program. Application required.",
      },
    ],
  },
  {
    id: 'jasleni',
    name: 'Jasleni',
    fullName: 'Jasleni Brito',
    role: 'Latina Artist & Confidence Coach',
    bio: 'Jasleni Brito is a Latina artist, confidence coach, and the creator of La Caribeña Oracle — a 50-card deck of original paintings channeling the wisdom, beauty, and resilience of the Caribbean. Raised in Santiago, Dominican Republic and now rooted in Connecticut, she draws from ancestral memory and spiritual wisdom. For Jasleni, paint is prayer and color is language.',
    image: '/creators/jasleni.webp',
    // Creator-level CTA points at her oracle-reading booking; each service has its own url.
    bookingUrl: 'https://www.jasleniart.com/oracle-reading',
    status: 'live',
    featuredServices: [
      { title: 'Oracle Reading with Jasleni', price: 'Book' },
      { title: 'La Caribeña Oracle Deck', price: 'Order' },
    ],
    allServices: [
      {
        title: 'Oracle Reading with Jasleni',
        price: 'Book',
        url: 'https://www.jasleniart.com/oracle-reading',
        description: 'A personal oracle reading with Jasleni, channeling the wisdom of La Caribeña.',
      },
      {
        title: 'The Art of Becoming — Confidence Coaching',
        price: 'Apply',
        url: 'https://www.jasleniart.com/the-art-of-becoming',
        description: 'Coaching for big dreamers who are done playing small and ready to finally go for it.',
      },
      {
        title: 'La Caribeña Oracle Deck',
        price: 'Order',
        url: 'https://www.jasleniart.com/product-page/la-caribe%C3%B1a-oracle-deck',
        description: "Jasleni's 50-card oracle deck of original paintings, mailed to your door.",
      },
    ],
  },
]

/** Creators with status: 'live' (or no status — backwards compat).
 *  Use this in any UI that hasn't been updated to render the
 *  joining-soon state. The full CREATORS list (which now includes
 *  joining-soon entries like Jasleni) is consumed only by the new
 *  landing-v2 PractitionersStrip in Milestone 3. */
export const LIVE_CREATORS: Creator[] = CREATORS.filter(
  (c) => c.status !== 'joining-soon',
)
